import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import type { Invoice } from "@/lib/invoice.api";
import { cn } from "@/lib/utils";
import { fmtDate, lineSummary, money } from "@/pages/documents/components/DocumentWorkspace";
import { getInvoiceLabel } from "../invoice-formatters";
import { isInvoiceOverdue } from "../invoice-calculations";
import { InvoiceMiniFact } from "./InvoiceMiniFacts";
import { InvoiceStatusBadges } from "./InvoiceStatusBadges";

interface InvoiceGridProps {
  invoices: Invoice[];
  onSelect: (invoice: Invoice) => void;
}

export function InvoiceGrid({ invoices, onSelect }: InvoiceGridProps) {
  if (invoices.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucune facture.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className={cn(
            "rounded-lg border bg-card p-4",
            isInvoiceOverdue(invoice) ? "border-danger-border bg-danger-bg/30" : "border-border",
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <button type="button" className="min-w-0 text-left" onClick={() => onSelect(invoice)}>
              <p className="truncate text-[13px] font-semibold text-brand-gold-dark">
                {getInvoiceLabel(invoice)}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {invoice.partySnapshot?.fullName ?? "Client non renseigné"}
              </p>
            </button>
            <InvoiceStatusBadges
              invoice={invoice}
              includeOverdue
              className="flex shrink-0 flex-col items-end gap-1"
            />
          </div>

          <div className="mt-4">
            <p className="text-[22px] font-bold leading-tight tabular-nums text-foreground">
              {money(invoice.totalAmount)}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">{lineSummary(invoice.lines)}</p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-3">
            <InvoiceMiniFact label="Créée" value={fmtDate(invoice.createdAt)} />
            <InvoiceMiniFact label="Échéance" value={fmtDate(invoice.dueDate)} />
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(invoice)}>
              <Eye size={13} /> Aperçu
            </Button>
            <Link
              to={`/invoices/${invoice.id}`}
              className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
            >
              Détail
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
